import { esmaToTraitName } from '../../utils/terminologyMap'

interface Props {
  esma_profile?: Record<string, number>
  size?: number
}

export default function EsmaRadarChart({ esma_profile, size = 200 }: Props) {
  if (!esma_profile || Object.keys(esma_profile).length === 0) {
    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--body-bg)', borderRadius: 12, border: '1px dashed var(--border-color)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
          No personality data yet — agent is still growing
        </span>
      </div>
    )
  }

  // Top 8 traits
  const sorted = Object.entries(esma_profile)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
  const count = sorted.length
  const cx = size / 2
  const cy = size / 2
  const r = size * 0.38

  // Calculate points
  const points = sorted.map(([, score], i) => {
    const angle = (Math.PI * 2 * i) / count - Math.PI / 2
    const dist = r * Math.min(score, 1)
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) }
  })

  const polygon = points.map(p => `${p.x},${p.y}`).join(' ')

  // Grid circles
  const gridLevels = [0.25, 0.5, 0.75, 1.0]

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Grid */}
      {gridLevels.map(level => (
        <circle key={level} cx={cx} cy={cy} r={r * level}
          fill="none" stroke="var(--border-color)" strokeWidth={0.5} opacity={0.3} />
      ))}

      {/* Axes */}
      {sorted.map(([,], i) => {
        const angle = (Math.PI * 2 * i) / count - Math.PI / 2
        const ex = cx + r * Math.cos(angle)
        const ey = cy + r * Math.sin(angle)
        return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey} stroke="var(--border-color)" strokeWidth={0.5} opacity={0.3} />
      })}

      {/* Filled area */}
      <polygon points={polygon} fill="#8B5CF630" stroke="#8B5CF6" strokeWidth={1.5} />

      {/* Labels */}
      {sorted.map(([name], i) => {
        const angle = (Math.PI * 2 * i) / count - Math.PI / 2
        const lx = cx + (r + 18) * Math.cos(angle)
        const ly = cy + (r + 18) * Math.sin(angle)
        const trait = esmaToTraitName(name)
        return (
          <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
            fontSize={8} fill="var(--text-secondary)" fontWeight={500}>
            {trait.length > 10 ? trait.substring(0, 9) + '…' : trait}
          </text>
        )
      })}

      {/* Score dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill="#8B5CF6" />
      ))}
    </svg>
  )
}
